import { useEffect, useState } from "react";

import { getPdfWorkerSrc } from "config/env";
import type { PDFDocumentProxy } from "pdfjs-dist";

// pdf.js reads the PDF in chunks of this size with range requests, so only the shown pages are downloaded.
const RANGE_CHUNK_SIZE = 65536;
// A PDF stays loaded this long after its last viewer closes, so switching views or reopening it doesn't download it again.
const RELEASE_DELAY_MS = 60000;

type LoadedPdf = { promise: Promise<PDFDocumentProxy>; viewerCount: number; releaseTimer?: ReturnType<typeof setTimeout> };

const loadedPdfs = new Map<string, LoadedPdf>();

const loadPdf = (pdfUrl: string) =>
  import("pdfjs-dist")
    .then((pdfjsLib) => {
      pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfWorkerSrc(pdfjsLib.version);
      return pdfjsLib.getDocument({ url: pdfUrl, disableAutoFetch: true, disableStream: true, rangeChunkSize: RANGE_CHUNK_SIZE }).promise;
    })
    .catch((error) => {
      // A failed load isn't kept, so the next viewer tries again.
      loadedPdfs.delete(pdfUrl);
      throw error;
    });

const acquirePdf = (pdfUrl: string) => {
  const loadedPdf = loadedPdfs.get(pdfUrl) ?? { promise: loadPdf(pdfUrl), viewerCount: 0 };
  clearTimeout(loadedPdf.releaseTimer);
  loadedPdf.viewerCount += 1;
  loadedPdfs.set(pdfUrl, loadedPdf);
  return loadedPdf.promise;
};

const releasePdf = (pdfUrl: string) => {
  const loadedPdf = loadedPdfs.get(pdfUrl);
  if (!loadedPdf) return;
  loadedPdf.viewerCount -= 1;
  if (loadedPdf.viewerCount > 0) return;

  loadedPdf.releaseTimer = setTimeout(() => {
    loadedPdfs.delete(pdfUrl);
    // A PDF that failed to load has nothing to free.
    loadedPdf.promise.then(
      (pdf) => pdf.destroy(),
      () => undefined
    );
  }, RELEASE_DELAY_MS);
};

/** Loads a PDF with pdf.js. Viewers of the same URL share one loaded PDF, which is freed a minute after the last one closes. */
export const usePdfDocument = (pdfUrl: string) => {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    setPdf(null);
    setHasError(false);

    acquirePdf(pdfUrl)
      .then((document) => !isCancelled && setPdf(document))
      .catch(() => !isCancelled && setHasError(true));

    return () => {
      isCancelled = true;
      releasePdf(pdfUrl);
    };
  }, [pdfUrl]);

  return { pdf, hasError };
};
